import { Currency, CurrencyAmount, Percent, Token } from '@kodiak-finance/sdk-core'
import { Pair } from '@kodiak-finance/v2-sdk'
import { AutoColumn } from 'components/Column'
import Loader from 'components/Loader'
import { LoadingOpacityContainer, loadingOpacityMixin } from 'components/Loader/styled'
import { isSupportedChain } from 'config/chains'
import { useConnection } from 'hooks/useConnection'
import { darken, lighten } from 'polished'
import { HTMLAttributes, ReactNode, useCallback, useMemo, useState } from 'react'
import { Lock } from 'react-feather'
import styled from 'styled-components'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'

import { ReactComponent as DropDown } from '../../assets/images/dropdown.svg'
import { ReactComponent as Wallet } from '../../assets/svg/icons/Wallet.svg'
import { REFETCH_POLICY_FAST } from '../../config/RefetchPolicies'
import useTheme from '../../hooks/useTheme'
import { useCurrencyBalance } from '../../state/connection/hooks'
import { ThemedText } from '../../theme'
import { ButtonGray } from '../Button'
import CurrencyLogo from '../CurrencyLogo'
import DoubleCurrencyLogo from '../DoubleLogo'
import { Input as NumericalInput } from '../NumericalInput'
import Row, { RowBetween, RowFixed } from '../Row'
import CurrencySearchModal from '../SearchModal/CurrencySearchModal'
import { FiatValue } from './FiatValue'

const InputPanel = styled.div<{ hideInput?: boolean }>`
  ${({ theme }) => theme.flexColumnNoWrap}
  position: relative;
  border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')};
  background-color: ${({ theme, hideInput }) => (hideInput ? 'transparent' : theme.deprecated_bg2)};
  z-index: 1;
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  transition: height 1s ease;
  will-change: height;
`

const FixedContainer = styled.div<{ hideBorder?: boolean }>`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: ${({ theme }) => theme.cardSecondary};
  border-radius: 10px;
  border: ${({ hideBorder, theme }) => (!hideBorder ? `1px solid ${theme.cardBorderPrimary}` : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: height 0.3s ease-out;
`

const Container = styled.div<{
  hideBorder?: boolean
  disabled?: boolean
  hideInput?: boolean
}>`
  /* border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')}; */
  background-color: ${({ theme }) => theme.cardSecondary};
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  border-radius: ${({ hideBorder }) => (!hideBorder ? '8px' : '0px')};
  border: ${({ hideBorder, theme }) => (!hideBorder ? `1px solid ${theme.cardBorderPrimary}` : 'none')};
  display: flex;
  flex-direction: column;
  justify-content: space-between; /* This will help distribute content evenly */
`

export const BalanceFlex = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const CurrencySelect = styled(ButtonGray)<{
  visible: boolean
  selected: boolean
  hideInput?: boolean
  disabled?: boolean
}>`
  align-items: center;
  background-color: ${({ selected, theme }) => (selected ? theme.deprecated_bg2 : theme.deprecated_primary1)};
  opacity: ${({ disabled }) => (!disabled ? 1 : 0.4)};
  box-shadow: ${({ selected }) => (selected ? 'none' : '0px 6px 10px rgba(0, 0, 0, 0.075)')};
  color: ${({ selected, theme }) => (selected ? theme.deprecated_text1 : theme.deprecated_white)};
  cursor: pointer;
  height: ${({ hideInput }) => (hideInput ? '2.8rem' : '2.4rem')};
  border-radius: 16px;
  outline: none;
  user-select: none;
  border: none;
  /* font-size: 24px;
  font-weight: 400; */
  /* label/label-medium */

  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  padding: 0 8px;
  gap: 0px;
  justify-content: space-between;
  margin-left: ${({ hideInput }) => (hideInput ? '0' : '12px')};
  :focus,
  :hover {
    background-color: ${({ selected, theme }) =>
      selected ? theme.deprecated_bg3 : darken(0.05, theme.deprecated_primary1)};
  }
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`
export const InputCurrencySelect = styled(CurrencySelect)`
  align-items: center;
  background-color: ${({ selected, theme }) => theme.buttonSubtle1};
  color: ${({ theme }) => theme.textPrimary};
  height: 28px;
  border-radius: 4px;
  padding: 4px;
  gap: 16px;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */

  :focus,
  :hover {
    background-color: ${({ selected, theme }) => lighten(0.05, theme.buttonSubtle1)};
  }
`

const InputRow = styled.div<{ selected: boolean }>`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  justify-content: space-between;
  padding: ${({ selected }) => (selected ? ' 1rem 1rem 0.75rem 1rem' : '1rem 1rem 1rem 1rem')};
`

const LabelRow = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  color: ${({ theme }) => theme.deprecated_text1};
  font-size: 0.75rem;
  line-height: 1rem;
  padding: 0 1rem 1rem;

  span:hover {
    cursor: pointer;
    color: ${({ theme }) => darken(0.2, theme.deprecated_text2)};
  }
`

const FiatRow = styled(LabelRow)`
  justify-content: flex-end;
  height: '24px';
`

const NoBalanceState = styled.div`
  color: ${({ theme }) => theme.textTertiary};
  font-weight: 400;
  justify-content: space-between;
  padding: 0px 4px;
`
const NoBalanceDash = styled.span`
  color: ${({ theme }) => theme.textTertiary};
  font-variant: small-caps;
  font-feature-settings: 'pnum' on, 'lnum' on;
`

export const Aligner = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

export const StyledDropDown = styled(DropDown)<{ selected: boolean }>`
  margin: 0 0.25rem 0 0.35rem;
  height: 35%;

  path {
    stroke: ${({ theme }) => theme.textPrimary};
    stroke-width: 1.5px;
  }
`

export const StyledTokenName = styled.span<{ active?: boolean }>`
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  margin-left: 4px;
`

const StyledBalanceMax = styled.button<{ disabled?: boolean }>`
  background-color: transparent;
  background-color: ${({ theme }) => theme.buttonSubtle2};
  border: none;
  text-transform: uppercase;
  border-radius: 12px;
  color: ${({ theme }) => theme.accentAction};
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  margin-left: 0.25rem;
  opacity: ${({ disabled }) => (!disabled ? 1 : 0.4)};
  padding: 4px 6px;
  pointer-events: ${({ disabled }) => (!disabled ? 'initial' : 'none')};

  :hover {
    opacity: ${({ disabled }) => (!disabled ? 0.8 : 0.4)};
  }

  :focus {
    outline: none;
  }
`

const StyledNumericalInput = styled(NumericalInput)<{ $loading: boolean }>`
  ${loadingOpacityMixin};
  text-align: left;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
`

export const PercentButtonRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  gap: 48px;
`

export const PercentButton = styled.button`
  height: 28px;
  flex: 1;
  background-color: ${({ theme }) => theme.cardPrimary};
  color: ${({ theme }) => theme.deprecated_text1};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.deprecated_bg3};
  outline: none;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.cardTertiary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
const InlinePercentButton = styled.button`
  height: 20px;
  padding: 2px 6px;
  background-color: ${({ theme }) => theme.buttonSubtle2};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.buttonSubtle1};
    color: ${({ theme }) => theme.textPrimary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const InlinePercentButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
`

interface PercentButtonHandlers {
  onSelect10Percent: () => void
  onSelect25Percent: () => void
  onSelect50Percent: () => void
  onSelectMax: () => void
}

interface CurrencyInputPanelProps {
  value: string
  onUserInput?: (value: string) => void
  onMax?: () => void
  showMaxButton: boolean
  label?: ReactNode
  onCurrencySelect?: (currency: Currency) => void
  currency?: Currency | null
  hideBalance?: boolean
  pair?: Pair | null
  hideInput?: boolean
  otherCurrency?: Currency | null
  fiatValue?: CurrencyAmount<Token> | null
  apiUSDValue?: string
  priceImpact?: Percent
  id: string
  showCommonBases?: boolean
  showCurrencyAmount?: boolean
  disableNonToken?: boolean
  renderBalance?: (amount: CurrencyAmount<Currency>) => ReactNode
  locked?: boolean
  lockedMessage?: string
  loading?: boolean
  hideBorder?: boolean
  allowedCurrencies?: string[]
  allowNativeToken?: boolean
  showPercentButtons?: boolean
  percentButtonHandlers?: PercentButtonHandlers
  isSwapInterface?: boolean
  preferredCurrencies?: Currency[]
  containerProps?: HTMLAttributes<HTMLDivElement>
  disabled?: boolean
  showCategories?: boolean
  compactSearchModal?: boolean
}

export default function CurrencyInputPanel({
  value,
  onUserInput,
  onMax,
  showMaxButton,
  onCurrencySelect,
  currency,
  otherCurrency,
  id,
  showCommonBases,
  showCurrencyAmount: showCurrencyAmountProperty,
  disableNonToken,
  renderBalance,
  fiatValue,
  apiUSDValue,
  priceImpact,
  hideBorder,
  hideBalance = false,
  pair = null, // used for double token logo
  hideInput = false,
  locked = false,
  lockedMessage,
  loading = false,
  allowedCurrencies,
  allowNativeToken = true,
  showPercentButtons = false,
  isSwapInterface = false,
  percentButtonHandlers,
  preferredCurrencies,
  containerProps,
  disabled,
  showCategories,
  compactSearchModal,
  ...rest
}: CurrencyInputPanelProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const { account, chainId } = useConnection()
  const selectedCurrencyBalance = useCurrencyBalance(account ?? undefined, currency ?? undefined, REFETCH_POLICY_FAST)
  const theme = useTheme()

  const showCurrencyAmount = useMemo(() => {
    if (showCurrencyAmountProperty !== undefined) {
      return showCurrencyAmountProperty
    }
    return Boolean(account)
  }, [showCurrencyAmountProperty, account])

  const handleDismissSearch = useCallback(() => {
    setModalOpen(false)
  }, [setModalOpen])

  const handleSelectPercent = useCallback(
    (percent: number) => {
      if (selectedCurrencyBalance) {
        const percentAmount = selectedCurrencyBalance.multiply(percent * 100).divide(100)
        onUserInput?.(percentAmount.toExact())
      }
    },
    [selectedCurrencyBalance, onUserInput]
  )

  const chainAllowed = isSupportedChain(chainId)

  return (
    <InputPanel id={id} hideInput={hideInput} {...rest}>
      {locked && (
        <FixedContainer>
          <AutoColumn gap="sm" justify="center">
            <Lock />
            <ThemedText.DeprecatedLabel fontSize="12px" textAlign="center" padding="0 12px">
              <>
                {lockedMessage
                  ? lockedMessage
                  : 'The market price is outside your specified price range. Single-asset deposit only.'}
              </>
            </ThemedText.DeprecatedLabel>
          </AutoColumn>
        </FixedContainer>
      )}
      <Container hideBorder={hideBorder} disabled={!chainAllowed} className="input-container" {...containerProps}>
        <InputRow style={hideInput ? { padding: '0', borderRadius: '8px' } : {}} selected={!onCurrencySelect}>
          {!hideInput && (
            <>
              <StyledNumericalInput
                className="token-amount-input"
                value={value}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onUserInput={onUserInput ?? (() => {})}
                disabled={disabled || !chainAllowed}
                $loading={loading}
              />
              {loading && <Loader />}
            </>
          )}

          <InputCurrencySelect
            disabled={!chainAllowed}
            visible={currency !== undefined}
            selected={!!currency}
            hideInput={hideInput}
            className="open-currency-select-button"
            onClick={() => {
              if (onCurrencySelect) {
                setModalOpen(true)
              }
            }}
          >
            <Aligner>
              <RowFixed>
                {pair ? (
                  <span style={{ marginRight: '0.5rem' }}>
                    <DoubleCurrencyLogo currency0={pair.token0} currency1={pair.token1} size={24} margin={true} />
                  </span>
                ) : currency ? (
                  <CurrencyLogo className="mr-0.5" currency={currency} size="20px" />
                ) : null}
                {pair ? (
                  <StyledTokenName className="pair-name-container">
                    {pair?.token0.symbol}:{pair?.token1.symbol}
                  </StyledTokenName>
                ) : (
                  <StyledTokenName className="token-symbol-container" active={Boolean(currency && currency.symbol)}>
                    {(currency && currency.symbol && currency.symbol.length > 20
                      ? currency.symbol.slice(0, 4) +
                        '...' +
                        currency.symbol.slice(currency.symbol.length - 5, currency.symbol.length)
                      : currency?.symbol) || <>Select token</>}
                  </StyledTokenName>
                )}
              </RowFixed>
              {onCurrencySelect && <StyledDropDown selected={!!currency} />}
            </Aligner>
          </InputCurrencySelect>
        </InputRow>
        {!hideInput && !hideBalance && currency && (
          <FiatRow>
            <RowBetween>
              <LoadingOpacityContainer $loading={loading}>
                <FiatValue fiatValue={fiatValue} apiUSDValue={apiUSDValue} priceImpact={priceImpact} />
              </LoadingOpacityContainer>
              {account ? (
                <RowFixed style={{ height: '17px' }}>
                  <ThemedText.DeprecatedBody
                    onClick={onMax}
                    color={theme.deprecated_text3}
                    fontWeight={500}
                    fontSize={14}
                    style={{ display: 'inline', cursor: 'pointer' }}
                  >
                    {!hideBalance && currency && selectedCurrencyBalance ? (
                      renderBalance ? (
                        renderBalance(selectedCurrencyBalance)
                      ) : (
                        <BalanceFlex>
                          <Wallet height={18} width={18} />
                          <>{formatCurrencyAmount(selectedCurrencyBalance, 4)}</>
                        </BalanceFlex>
                      )
                    ) : null}
                  </ThemedText.DeprecatedBody>
                  {showMaxButton && selectedCurrencyBalance ? (
                    <StyledBalanceMax onClick={onMax}>
                      <>Max</>
                    </StyledBalanceMax>
                  ) : null}
                </RowFixed>
              ) : (
                <span />
              )}
            </RowBetween>
          </FiatRow>
        )}

        {showPercentButtons && !hideInput && currency && (
          <PercentButtonRow>
            <Row gap="8px">
              <PercentButton onClick={percentButtonHandlers?.onSelect10Percent || (() => handleSelectPercent(0.1))}>
                10%
              </PercentButton>
              <PercentButton onClick={percentButtonHandlers?.onSelect25Percent || (() => handleSelectPercent(0.25))}>
                25%
              </PercentButton>
              <PercentButton onClick={percentButtonHandlers?.onSelect50Percent || (() => handleSelectPercent(0.5))}>
                50%
              </PercentButton>
              <PercentButton onClick={percentButtonHandlers?.onSelectMax || onMax}>Max</PercentButton>
            </Row>
          </PercentButtonRow>
        )}
      </Container>
      {onCurrencySelect && (
        <CurrencySearchModal
          isOpen={modalOpen}
          onDismiss={handleDismissSearch}
          onCurrencySelect={onCurrencySelect}
          selectedCurrency={currency}
          otherSelectedCurrency={otherCurrency}
          showCommonBases={showCommonBases}
          showCurrencyAmount={showCurrencyAmount}
          disableNonToken={disableNonToken}
          allowedCurrencies={allowedCurrencies}
          allowNativeToken={allowNativeToken}
          preferredCurrencies={preferredCurrencies}
          showCategories={showCategories}
          compactHeight={compactSearchModal}
        />
      )}
    </InputPanel>
  )
}
